import {
  HttpBackend,
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from "@angular/common/http";
import { inject, Injectable, OnDestroy } from "@angular/core";
import { Router } from "@angular/router";
import { Store } from "@ngrx/store";
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  Observable,
} from "rxjs";
import { distinctUntilChanged, map } from "rxjs/operators";

import { environment } from "../../../environments/environment";
import { AuthenticationActions } from "../store/authentication";
import { AppState } from "../store/store";
import { validateRelativeUrl } from "../utils/url-security";
import { AuthenticationService } from "./authentication.service";
import { LocalStorageService } from "./browser-apis/local-storage.service";
import { WindowService } from "./browser-apis/window.service";
import { NotificationService } from "./notification.service";

interface AvailableProduct {
  id: string;
  name: string;
}

interface UserInfoResponse {
  sub: string;
  expiresAt: number | null;
  product: string;
  role: string;
  availableProducts: AvailableProduct[];
}

interface RefreshResponse {
  expiresAt: number | null;
  role: string;
}

enum RefreshOutcome {
  Success = "success",
  Unauthorized = "unauthorized",
  Retriable = "retriable",
}

const PRODUCT_CLAIM = "https://claims.du.com/product";
const ROLE_CLAIM = "https://claims.du.com/role";
const PRODUCT_KEY = "product";
const REFRESH_LIFETIME_FRACTION = 0.5;
const REFRESH_MIN_DELAY_MS = 5_000;
const REFRESH_RETRY_DELAY_MS = 30_000;
const LIVENESS_PROBE_INTERVAL_MS = 5 * 60_000;
const LOGIN_GUARD_TIMEOUT_MS = 5_000;

// To redirect after sign-in or sign-out, the API requires a path and a target
function uiReturnParams(returnUrl: string): URLSearchParams {
  const params = new URLSearchParams();
  params.set("returnUrl", returnUrl);
  params.set("returnTarget", "ui");
  return params;
}

@Injectable({ providedIn: "root" })
export class PortalAuthenticationService
  extends AuthenticationService
  implements OnDestroy
{
  // Use HttpBackend directly to bypass authInterceptor — internal auth
  // calls (/userinfo, /refresh) must not re-enter handlePortalUnauthorized,
  // which would recursively coalesce on this.refreshInFlight and deadlock
  // when /refresh returns 401. probeAuthMode uses the same pattern.
  private http = new HttpClient(inject(HttpBackend));
  // switchProduct uses the DI HttpClient (through authInterceptor) — unlike
  // /userinfo and /refresh — so a 401 on /switch-product flows through the
  // interceptor's recovery/teardown ladder (refresh→retry→login).
  private httpWithInterceptor = inject(HttpClient);
  private localStorageService = inject(LocalStorageService);
  private windowService = inject(WindowService);
  private router = inject(Router);
  private store = inject<Store<AppState>>(Store);
  private notificationService = inject(NotificationService);

  private isAuthenticatedSubject$ = new BehaviorSubject<boolean>(false);
  public override isAuthenticated$ = this.isAuthenticatedSubject$
    .asObservable()
    .pipe(distinctUntilChanged());

  private isDoneLoadingSubject$ = new BehaviorSubject<boolean>(false);
  public override isDoneLoading$ = this.isDoneLoadingSubject$.asObservable();

  public override canActivateProtectedRoutes$: Observable<boolean> =
    combineLatest([this.isAuthenticated$, this.isDoneLoading$]).pipe(
      map(values => values.every(b => b))
    );

  private userInfo: UserInfoResponse | null = null;
  private refreshTimer: ReturnType<typeof setTimeout> | null = null;
  private refreshInFlight: Promise<RefreshOutcome> | null = null;
  private loginInProgress = false;
  private loginGuardTimeout: ReturnType<typeof setTimeout> | null = null;

  ngOnDestroy(): void {
    this.clearRefreshTimer();
    this.releaseLoginGuard();
  }

  public override async runInitialLoginSequence(): Promise<void> {
    try {
      const info = await firstValueFrom(
        this.http.get<UserInfoResponse>(
          `${environment.developmentUrl}userinfo`,
          { withCredentials: true }
        )
      );
      this.userInfo = info;
      this.scheduleRefresh();
      this.isAuthenticatedSubject$.next(this.hasValidToken());
      this.store.dispatch(AuthenticationActions.loginSuccess());
    } catch (err) {
      if (err instanceof HttpErrorResponse && err.status === 401) {
        await this.login();
        return;
      }
      // Don't swallow 5xx / network errors from /userinfo — without a log,
      // an API outage at startup is indistinguishable from a normal
      // unauthenticated session.
      console.error("PortalAuthenticationService: /userinfo failed", err);
      this.isAuthenticatedSubject$.next(false);
    } finally {
      // Flip isDoneLoading$ regardless of path so canActivateProtectedRoutes$
      // unblocks even when the 401 → login() redirect doesn't actually fire
      // (test envs, popup-blocked browsers). Production navigates away
      // before any subscriber observes this push.
      this.isDoneLoadingSubject$.next(true);
    }
  }

  public override isAuthenticated(): boolean {
    return this.hasValidToken();
  }

  public override login(targetUrl?: string): Promise<boolean> {
    if (this.loginInProgress) {
      return Promise.resolve(false);
    }
    this.loginInProgress = true;

    // Backstop for the one failure the catch below cannot see: locationReplace
    // returns normally but the navigation never commits (popup-blocked
    // browsers, test environments). Without it a single such attempt would
    // wedge the guard for the lifetime of the (root-scoped) service and every
    // later attempt would return false without redirecting. The 5s delay is
    // irrelevant on the happy path because the page has already navigated away.
    this.loginGuardTimeout = setTimeout(
      () => this.releaseLoginGuard(),
      LOGIN_GUARD_TIMEOUT_MS
    );

    // Teardown belongs after loginInProgress is set: it pushes false onto
    // isAuthenticated$ before returning, and LoginGuard subscribes to that
    // stream and calls login() on a false push, so with the flag already set
    // that re-entrant call returns at the guard above instead of firing a
    // second redirect. Teardown stays ahead of the redirect setup so a throw
    // there can't leave a stale identity cached; the params below come from
    // localStorage and the router, not userInfo.
    this.tearDownSession();

    try {
      const returnUrl = this.resolveReturnUrl(targetUrl);
      const product = this.storedProduct();
      const params = uiReturnParams(returnUrl);
      if (product) {
        params.set("product", product);
      }

      this.windowService.locationReplace(
        `${environment.developmentUrl}login?${params.toString()}`
      );
    } catch (err) {
      // No redirect started, so release the guard now instead of making callers
      // wait out the backstop: while loginInProgress is set doSwitchProduct
      // stays silent and any other login() returns false without redirecting.
      this.releaseLoginGuard();
      throw err;
    }

    // true = redirect initiated. Matches ExternalAuthenticationService.login()
    // so future callers branching on the return value behave consistently
    // across both auth modes.
    return Promise.resolve(true);
  }

  public override logout(): void {
    const params = uiReturnParams("/");
    const action = `${environment.developmentUrl}logout?${params.toString()}`;

    // A refresh landing after /logout re-mints the cookie it cleared, and the
    // page runs until the response commits, so a scheduled one could still fire.
    this.clearRefreshTimer();

    // A second click posts a second form, but the browser commits one
    // navigation and /logout is idempotent. Blocking repeat clicks would
    // leave a failed sign-out impossible to retry, so they are not blocked.
    this.windowService.submitFormPost(action);
  }

  // Retain the boolean AuthenticationService contract while background refresh
  // consumes the detailed outcome.
  public override refresh(): Promise<boolean> {
    return this.attemptRefresh().then(
      outcome => outcome === RefreshOutcome.Success
    );
  }

  // Scheduled refresh needs the detailed outcome so transient failures retry
  // without treating the session as lost.
  private async refreshInBackground(): Promise<void> {
    try {
      const outcome = await this.attemptRefresh();
      if (outcome === RefreshOutcome.Unauthorized) {
        await this.login();
      } else if (outcome === RefreshOutcome.Retriable) {
        this.armRefreshTimer(REFRESH_RETRY_DELAY_MS);
      }
    } catch (err) {
      // The timer discards this promise, so contain login failures to prevent
      // unhandled rejections.
      console.error(
        "PortalAuthenticationService: background refresh failed",
        err
      );
    }
  }

  private attemptRefresh(): Promise<RefreshOutcome> {
    // Coalesce concurrent calls: parallel 401s on different requests would
    // otherwise fan out into N parallel POST /refresh hits, and backends
    // that atomically rotate cookies can invalidate each other's
    // freshly-issued cookies. Returning the same in-flight promise keeps
    // it to one POST.
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }
    this.refreshInFlight = this.doRefresh().finally(() => {
      this.refreshInFlight = null;
    });
    return this.refreshInFlight;
  }

  private async doRefresh(): Promise<RefreshOutcome> {
    // A refresh extends an already-established session. A null userInfo means
    // no session was bootstrapped in this SPA instance, so there's nothing to
    // extend — force the caller down the login path instead, which runs the
    // full /userinfo bootstrap from scratch.
    if (!this.userInfo) {
      return RefreshOutcome.Unauthorized;
    }

    let result: RefreshResponse;
    try {
      const product = this.currentProduct();
      const params = product
        ? new HttpParams().set("product", product)
        : undefined;
      result = await firstValueFrom(
        this.http.post<RefreshResponse>(
          `${environment.developmentUrl}refresh`,
          null,
          { params, withCredentials: true }
        )
      );
    } catch (err) {
      // Centralize 401 teardown here because only the refresh attempt knows the
      // cookie is invalid.
      if (err instanceof HttpErrorResponse && err.status === 401) {
        this.tearDownSession();
        return RefreshOutcome.Unauthorized;
      }
      // A teardown can null userInfo while the POST is open, the same race the
      // success path below re-reads for. Report the session as gone rather than
      // retriable: a retry would re-arm the timer tearDownSession just cleared,
      // and it would fire against a session the app has already given up on.
      if (!this.userInfo) {
        return RefreshOutcome.Unauthorized;
      }
      // Any other failure (network blip, 5xx) says nothing about the session,
      // so leave state intact and let the caller decide whether to retry.
      return RefreshOutcome.Retriable;
    }

    // A teardown can null userInfo mid-POST, and the earlier guard's narrowing
    // survives the await — re-read it.
    const session = this.userInfo;
    if (!session) {
      return RefreshOutcome.Unauthorized;
    }
    this.userInfo = {
      ...session,
      expiresAt: result.expiresAt,
      role: result.role,
    };
    this.scheduleRefresh();
    this.isAuthenticatedSubject$.next(this.hasValidToken());
    this.store.dispatch(AuthenticationActions.loginSuccess());
    return RefreshOutcome.Success;
  }

  // Clear the redirect-in-flight flag and its backstop timer together so the
  // two can never disagree.
  private releaseLoginGuard(): void {
    if (this.loginGuardTimeout) {
      clearTimeout(this.loginGuardTimeout);
      this.loginGuardTimeout = null;
    }
    this.loginInProgress = false;
  }

  // Clear cached identity to keep synchronous reads consistent with
  // isAuthenticated$, and cancel retries against the rejected cookie.
  private tearDownSession(): void {
    this.clearRefreshTimer();
    this.userInfo = null;
    this.isAuthenticatedSubject$.next(false);
  }

  private storedProduct(): string | null {
    let stored: string | null;
    try {
      stored = this.localStorageService.getItem(PRODUCT_KEY);
    } catch (err) {
      // Reading localStorage throws when the browser blocks site data for the
      // origin. Swallow it: login() calls this on the way to the redirect, and
      // AuthGuard calls login() synchronously, so a throw would fail the
      // navigation and leave no route to the login page. product is optional
      // in the login URL, so dropping it still redirects correctly.
      console.warn(
        "PortalAuthenticationService: could not read the stored product",
        err
      );
      return null;
    }
    if (stored && stored !== "null" && stored !== "undefined") {
      return stored;
    }
    return null;
  }

  private currentProduct(): string | null {
    return this.storedProduct() ?? this.userInfo?.product ?? null;
  }

  public override hasValidToken(): boolean {
    if (!this.userInfo) {
      return false;
    }
    const { expiresAt } = this.userInfo;
    // Null expiresAt is a backend-contract violation (the value should be
    // read from the cookie's exp claim). scheduleRefresh logs the breakage
    // and arms a liveness probe; here we still return true because flipping
    // to false would loop the user through /login while the backend treats
    // the cookie as valid.
    if (expiresAt == null) {
      return true;
    }
    return expiresAt * 1000 > Date.now();
  }

  public override accessToken(): Promise<string> {
    return Promise.resolve("");
  }

  public override switchProduct(product: string): void {
    void this.doSwitchProduct(product);
  }

  private async doSwitchProduct(product: string): Promise<void> {
    try {
      const params = new HttpParams().set("product", product);
      await firstValueFrom(
        this.httpWithInterceptor.post(
          `${environment.developmentUrl}switch-product`,
          null,
          { params, withCredentials: true }
        )
      );
    } catch (err) {
      // The interceptor owns 401 recovery/teardown, so two error shapes reach
      // here while it is already redirecting to login, and both stay silent
      // because a toast would flash up moments before the page navigates away:
      //   - HttpErrorResponse(401): refresh succeeded but the retried POST 401'd
      //     again, so the interceptor rethrew the 401 after calling login().
      //   - a plain Error: refresh failed, so the interceptor called login()
      //     and threw a non-HttpErrorResponse instead.
      // Both set loginInProgress on the way past, so that flag is the signal
      // for "a redirect is in flight" — anything reaching here without it means
      // no redirect is coming and the failure needs surfacing.
      if (err instanceof HttpErrorResponse) {
        // 403: authenticated, active user without access to the requested
        // product (e.g. a stale product list).
        if (err.status === 403) {
          this.notificationService.showError(
            "You no longer have access to that product."
          );
        } else if (err.status !== 401) {
          // 5xx / network: not interceptor-recoverable.
          this.notificationService.showError(
            "Unable to switch product. Please try again."
          );
        }
        // 401: interceptor-owned redirect already in flight — stay silent.
      } else if (!this.loginInProgress) {
        // The refresh itself rejected rather than resolving, which only happens
        // when a local side effect threw after the cookie was already re-minted.
        // The session is fine and no redirect is coming, so the switch just
        // failed and the user needs to know.
        this.notificationService.showError(
          "Unable to switch product. Please try again."
        );
      }
      return;
    }

    // Side effects in their own try so the HTTP handler above can't swallow
    // a storage or navigation failure. Persist only after success
    // so a failed switch never corrupts PRODUCT_KEY.
    // Response body unused — the reload re-derives product state via /userinfo.
    try {
      this.localStorageService.setItem(PRODUCT_KEY, product);

      // Path-only reload (no query/fragment): query params may be product-scoped,
      // so they could be stale after a switch, unlike login()'s router.url returnUrl.
      const pathname = this.windowService.getLocationPathname();
      const safeReloadUrl = validateRelativeUrl(pathname);
      this.windowService.locationReplace(safeReloadUrl);
    } catch {
      // The server already switched the product; only the local reload failed.
      this.notificationService.showError(
        "Product switched. Please refresh the page to continue."
      );
    }
  }

  public override get identityClaims(): object {
    if (!this.userInfo) {
      return {};
    }
    return {
      sub: this.userInfo.sub,
      [PRODUCT_CLAIM]: this.userInfo.product,
      [ROLE_CLAIM]: this.userInfo.role,
    };
  }

  public get availableProducts(): AvailableProduct[] {
    return this.userInfo?.availableProducts ?? [];
  }

  private resolveReturnUrl(targetUrl?: string): string {
    return validateRelativeUrl(targetUrl ?? this.router.url);
  }

  private scheduleRefresh(): void {
    if (!this.userInfo) {
      // All current callers set userInfo before invoking; this guard is
      // defensive in case a future caller doesn't. Clear any existing
      // timer since we have no session to schedule against.
      this.clearRefreshTimer();
      return;
    }
    const { expiresAt } = this.userInfo;
    if (expiresAt == null) {
      // Backend reads expiresAt from the cookie's exp claim and is expected
      // to always provide it. A null is a backend-contract violation worth
      // surfacing — and worth probing so we detect server-side cookie expiry
      // within a bounded window rather than sitting on a potentially-dead
      // cookie indefinitely.
      console.warn(
        "PortalAuthenticationService: userinfo returned without expiresAt; scheduling liveness probe"
      );
      this.armRefreshTimer(LIVENESS_PROBE_INTERVAL_MS);
      return;
    }
    const msRemaining = expiresAt * 1000 - Date.now();
    const msUntilRefresh = Math.max(
      msRemaining * REFRESH_LIFETIME_FRACTION,
      REFRESH_MIN_DELAY_MS
    );
    this.armRefreshTimer(msUntilRefresh);
  }

  private armRefreshTimer(delayMs: number): void {
    this.clearRefreshTimer();
    this.refreshTimer = setTimeout(() => {
      void this.refreshInBackground();
    }, delayMs);
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }
}
